import { Component, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, Input, Output, EventEmitter, ChangeDetectorRef, ViewChildren, QueryList, ViewChild, OnDestroy, inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Form, FormField, FormDefinition, Forminator } from '@nuso/forms/data-access';
import { BehaviorSubject, Subject, Subscription, filter, startWith, tap } from 'rxjs';
import { AfterViewInit } from '@angular/core';
import { FormControlComponent } from '../form-control/form-control.component';

@Component({
    selector: 'nuso-form-view',
    templateUrl: './form-view.component.html',
    styleUrls: ['./form-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FormViewComponent<T = any> implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private ref = inject(ChangeDetectorRef);


  @Input() formDef: FormDefinition;
  @Input() form: Form;
  @Input() input: T | null;
  @Input() lastSaveSuccess = false;
  @Input() busyMessage: string | null;
  @Input() errorMessage: string | null;
  @Input() saveText = 'Save';
  @Input() cancelText = 'Cancel';
  @Input() focusFirst = false;
  @Input() enableSubmit = false;
  @Input() allowCancel = true;
  @Input() enableCancel = true;
  @Input() useCustomValidator = false;
  @Input() hideButtons = false;
  @Input() showFields = true;
  @Input() disableForm = false;
  @Input() enableHeaderPane = false;
  @Input() enablePane = false;
  @Input() headerPaneTitle = '';
  @Input() headerPaneIcon: string | null = null;
  @Input() emitInputChanges = false;

  @Output() submitted = new EventEmitter<T>();
  @Output() cancelled = new EventEmitter();
  @Output() enabledChanged = new EventEmitter<boolean>();
  @Output() formInit = new EventEmitter<Form>();
  @Output() formChanged = new EventEmitter<any>();
  @Output() actionButtonClicked = new EventEmitter<FormField>();
  @Output() fieldClicked = new EventEmitter<FormField>();

  @ViewChild('formDirective', { static: true }) private formDirective: NgForm;
  @ViewChildren(FormControlComponent) formRows: QueryList<FormControlComponent>;

  enabled$ = new BehaviorSubject<boolean>(false);

  $inputChanges = new Subject<T>();
  disableForm$ = new Subject<T>();
  disableFormSub: Subscription;

  setEnabled(enabled: boolean) {
    this.enabled$.next(enabled);
    this.enabledChanged.emit(enabled);
    this.ref.markForCheck();
  }

  ngOnInit() {
    this.$inputChanges.pipe(
      startWith(this.input),
    )
    .subscribe(input => {
      this.setFormFields(input);
    })

    this.disableFormSub = this.disableForm$
      .pipe(
        startWith(this.disableForm),
        filter(disable => !!disable),
        tap(() => {
          this.form.group.disable();
          this.ref.markForCheck();
        }),
      )
      .subscribe();
  }

  ngOnDestroy() {
    if (this.disableFormSub) { this.disableFormSub.unsubscribe(); }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['formDef'] || changes['form']) {
      if (!this.form) {
        this.form = Forminator.createForm(this.formDef);
      }
      this.form.submitted = false;
      this.formInit.emit(this.form);

      this.form.group.valueChanges
        .pipe(
          tap(values => {
            this.formChanged.emit(values);
            this.setEnabled( this.isFormValid() );
          }),
        )
        .subscribe();
    }

    if (changes['enableSubmit']) {
      this.setEnabled(this.isFormValid());
    }

    if ( changes['input'] ) {
      this.$inputChanges.next(changes['input'].currentValue);
    }

    if (changes['lastSaveSuccess']) {
      this.saveSucceeded();
    }
  }

  // This component is OnPush, so a field mutated from outside -- options
  // fetched over the network, most often -- is there but unrendered until one
  // of its inputs happens to change. The field objects are passed down by
  // reference, so nothing about filling one in looks like a change from here.
  markChanged() {
    this.ref.markForCheck();
  }

  saveSucceeded() {
    this.form.group.markAsPristine();
  }

  isFormValid() {
    return this.useCustomValidator ? this.enableSubmit : this.form.group.valid;
  }

  ngAfterViewInit() {
    if (this.focusFirst) {
      const firstElement: FormControlComponent = this.formRows.first;
      firstElement.focus();
    }
  }

  busy(busyMessage = 'Saving') {
    this.busyMessage = busyMessage;
    this.errorMessage = null;
    this.ref.markForCheck();
  }

  complete(errorMessage: string | null = null) {
    this.errorMessage = errorMessage;
    this.busyMessage = null;
    if ( errorMessage ) {
      this.setEnabled(true);
    }
    this.ref.markForCheck();
  }

  onSave() {
    this.submitted.emit( this.submitForm() );
  }

  submitForm() {
    this.form.submitted = true;
    this.setEnabled(false);
    return Forminator.getTypedValues(this.form);
  }

  onCancel() {
    this.form.group.reset(this.input || {});
    this.setEnabled(false);
    this.complete();
    this.cancelled.emit();
  }

  reset() {
    Forminator.resetForm(this.form, this.input || {});
    this.formDirective.resetForm();
    this.setEnabled(false);
  }

  setFormFields(input: T | null) {
    this.form.group.reset({}, { emitEvent: false });
    Forminator.setValues(this.form, input, { emitEvent: this.emitInputChanges });
    this.setEnabled(this.enableSubmit ? this.form.group.valid : false);
  }
}
